import React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { EVORA_PURPLE, DARK_OLIVE } from '../../../theme/colors';

// Where the period rests relative to the "e" — lower-right, matching the logo.
const REST = '118deg';

// The olive period: rests at the logo spot, nudges backward (anticipation),
// swings a full turn, overshoots past the spot, settles back, then dwells.
const Orbit = styled('span')({
  position: 'absolute',
  inset: 0,
  transformOrigin: '50% 50%',
  animation: 'evoraPeriodNudge var(--evora-dur, 3.2s) linear infinite',
  '@keyframes evoraPeriodNudge': {
    '0%':   { transform: 'rotate(var(--evora-rest))', animationTimingFunction: 'cubic-bezier(.35,0,.3,1)' },
    '7%':   { transform: 'rotate(calc(var(--evora-rest) - 20deg))' },
    '12%':  { transform: 'rotate(calc(var(--evora-rest) - 20deg))', animationTimingFunction: 'cubic-bezier(.5,0,.12,1)' },
    '60%':  { transform: 'rotate(calc(var(--evora-rest) + 380deg))' },
    '67%':  { transform: 'rotate(calc(var(--evora-rest) + 380deg))', animationTimingFunction: 'cubic-bezier(.33,0,.2,1)' },
    '77%':  { transform: 'rotate(calc(var(--evora-rest) + 360deg))' },
    '100%': { transform: 'rotate(calc(var(--evora-rest) + 360deg))' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
    transform: 'rotate(var(--evora-rest))',
  },
});

const Dot = styled('span')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  borderRadius: '50%',
  background: DARK_OLIVE,
  transform: 'translate(-50%, -50%) translateY(calc(-1 * var(--evora-r)))',
});

// A motion trail of tapering ghost dots that follow the orbit path behind the
// period. The whole trail fades in only while in flight, so it reads as a comet
// mid-swing and parks as a clean dot at rest.
const TRAIL = [
  { a: 6,  s: 0.86, o: 0.55 },
  { a: 12, s: 0.68, o: 0.38 },
  { a: 19, s: 0.5,  o: 0.22 },
  { a: 27, s: 0.34, o: 0.1 },
];

const Trail = styled('span')({
  position: 'absolute',
  inset: 0,
  animation: 'evoraTailFlight var(--evora-dur, 3.2s) linear infinite',
  '@keyframes evoraTailFlight': {
    '0%':   { opacity: 0 },
    '12%':  { opacity: 0 },
    '26%':  { opacity: 1 },
    '58%':  { opacity: 1 },
    '66%':  { opacity: 0 },
    '100%': { opacity: 0 },
  },
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
});

// A full-size layer rotated back by a fixed angle so its dot sits behind the
// period along the circular path.
const GhostLayer = styled('span')({
  position: 'absolute',
  inset: 0,
  transformOrigin: '50% 50%',
});

const Ghost = styled('span')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  borderRadius: '50%',
  background: DARK_OLIVE,
  transform: 'translate(-50%, -50%) translateY(calc(-1 * var(--evora-r)))',
});

/**
 * Evora "spinning period" loader — a static "e" (app brand font) with the
 * olive-green period orbiting it, the brand's dot as the moving part. For
 * neutral surfaces: section/page loads, field adornments, cards. On coloured
 * buttons use CometSpinner instead (the period would not read on the gradient).
 *
 * Drop-in-shaped for MUI CircularProgress: honours `size` (px, default 40) and
 * `sx`. `eColor` sets the "e" colour (default brand purple); `speed` sets the
 * loop duration in seconds (default 3.2). MUI-only props
 * (color/thickness/variant/value/disableShrink) are accepted and ignored.
 */
const EvoraSpinner = React.forwardRef(function EvoraSpinner({
  size = 40,
  eColor = EVORA_PURPLE,
  speed = 3.2,
  sx,
  className,
  // MUI transitions (Fade/Grow/etc.) inject `style` for the animation — merge it
  // below rather than let it clobber our CSS-variable style.
  style,
  // swallow MUI CircularProgress props so they don't reach the DOM:
  color: _color, thickness: _thickness, variant: _variant, value: _value, disableShrink: _disableShrink,
  ...rest
}, ref) {
  const px = typeof size === 'number' ? size : parseFloat(size) || 40;
  const r = px * 0.40;
  const dot = Math.max(2.5, px * 0.15);
  const fs = px * 0.74;

  return (
    <Box
      ref={ref}
      className={className}
      role="progressbar"
      aria-label="Loading"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: px,
        height: px,
        ...sx,
      }}
      style={{ '--evora-rest': REST, '--evora-r': `${r}px`, '--evora-dot': `${dot}px`, '--evora-dur': `${speed}s`, ...style }}
      {...rest}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          fontFamily: (theme) => theme.typography.fontFamily,
          fontWeight: 600,
          lineHeight: 1,
          letterSpacing: '-0.02em',
          fontSize: `${fs}px`,
          color: eColor,
          userSelect: 'none',
        }}
      >
        e
      </Box>
      <Orbit>
        <Trail>
          {TRAIL.map((g) => (
            <GhostLayer key={g.a} style={{ transform: `rotate(-${g.a}deg)` }}>
              <Ghost style={{ width: `${dot * g.s}px`, height: `${dot * g.s}px`, opacity: g.o }} />
            </GhostLayer>
          ))}
        </Trail>
        <Dot style={{ width: `${dot}px`, height: `${dot}px` }} />
      </Orbit>
    </Box>
  );
});

export default EvoraSpinner;
